/* Theme core: the part of the vendor `assets/vendor.js` (Minimog v6 webpack
   bundle, 135 KB) the theme still calls, rewritten with the same API. It
   replaced that file on 2026-09-25 (stage 3, step 3.4 of
   docs/_archiwum/plans/ANALIZA-wlasny-motyw-2026-09-25.md); original: git show 448840b:assets/vendor.js.

   - `window.LeonartoEvents` — pub/sub bus: subscribe(event, fn) returns an
     unsubscribe function, unSubscribe(event, fn), emit(event, ...args), evts.
   - `LeonartoLibs.AnimateLoading` — the loading bar and overlay of the cart
     drawer (`assets/cart-drawer-bento.js`); its styles are `assets/vendor.css`.
   - `LeonartoTheme.Notification.show({ target, type, message, method, last,
     delay, debug, sticky })` — the toast that `cart-drawer-bento.js`,
     `product-info-bento.js` and `product-card-bento.js` insert next to `target`; styles are the
     `.notification` rules of `assets/main.css`.

   Left out, because nothing in the theme calls them: Swiper, Accordion,
   Modal, Motion, scrollIntoView, loadjs, the JSX helper and the
   Element.append polyfill. The @ungap/custom-elements polyfill (Safari has
   no customized built-ins) went on 2026-09-25 with the last `is="…"`
   element, in step 3.5 (assets/cart-drawer-bento.js). */

(function () {
  'use strict';

  if (!window.LeonartoEvents) {
    window.LeonartoEvents = new (class {
      constructor() {
        this.events = {};
      }

      get evts() {
        return Object.keys(this.events);
      }

      subscribe(event, handler) {
        this.events[event] = this.events[event] || [];
        this.events[event].push(handler);
        return () => this.unSubscribe(event, handler);
      }

      unSubscribe(event, handler) {
        const handlers = this.events[event];
        if (!Array.isArray(handlers)) return;
        const index = handlers.indexOf(handler);
        if (index !== -1) handlers.splice(index, 1);
      }

      emit(event, ...args) {
        (this.events[event] || []).forEach((handler) => handler(...args));
      }
    })();
  }

  const libs = (window.LeonartoLibs = window.LeonartoLibs || {});
  const theme = (window.LeonartoTheme = window.LeonartoTheme || {});

  libs.AnimateLoading = class AnimateLoading {
    constructor(target = document.body, options = {}) {
      this.options = Object.assign(
        { overlay: null, thickness: '3px', color: 'gray', startDuration: 1000, finishDuration: 300 },
        options
      );
      this.target = target;
      this.overlay = this.options.overlay;
      this.setLoadingData();
    }

    setLoadingData = () => {
      const { startDuration, finishDuration, thickness, color } = this.options;
      const style = this.target.style;
      style.setProperty('--al-thickness', ` ${thickness}`);
      style.setProperty('--al-color', ` ${color}`);
      style.setProperty('--al-start-duration', ` ${startDuration}ms`);
      style.setProperty('--al-finish-duration', ` ${finishDuration}ms`);
    };

    start = () => {
      this.target.classList.add('al-loading-bar', 'start', 'loading');
      if (this.overlay) this.overlay.classList.add('al-loading-overlay', 'overlay-show');
    };

    finish = (callback = () => {}) => {
      const { target, overlay } = this;
      const { finishDuration } = this.options;
      // The bar animates from its current width to full, so freeze it first.
      target.style.setProperty('--al-end-width', window.getComputedStyle(target, ':before').width);
      target.classList.add('loaded');
      target.classList.remove('loading');
      window.requestAnimationFrame(() => {
        target.classList.add('finished');
        if (overlay) overlay.classList.remove('overlay-show');
      });
      setTimeout(this.cleanUp, finishDuration * 2);
      setTimeout(callback, finishDuration);
    };

    cleanUp = () => {
      this.target.classList.remove('al-loading-bar', 'start', 'loaded', 'finished');
      if (this.overlay) this.overlay.classList.remove('al-loading-overlay');
    };
  };

  const SVG_NS = 'http://www.w3.org/2000/svg';
  // Same faces as the vendor toast: a smile for success, a frown otherwise.
  const FACE_SMILE = 'M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z';
  const FACE_FROWN = 'M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z';

  function buildNotification({ type, message, onclick, sticky }) {
    const svg = document.createElementNS(SVG_NS, 'svg');
    [['class', 'm-svg-icon--large'], ['fill', 'none'], ['stroke', 'currentColor'], ['viewBox', '0 0 24 24'], ['xmlns', SVG_NS]]
      .forEach(([name, value]) => svg.setAttribute(name, value));
    const path = document.createElementNS(SVG_NS, 'path');
    [['stroke-linecap', 'round'], ['stroke-linejoin', 'round'], ['stroke-width', '2'], ['d', type === 'success' ? FACE_SMILE : FACE_FROWN]]
      .forEach(([name, value]) => path.setAttribute(name, value));
    svg.append(path);

    const text = document.createElement('div');
    text.className = 'notification__text';
    text.append(message);

    const noti = document.createElement('div');
    noti.className = `notification ${type}${sticky ? ' notification--sticky' : ''}`;
    noti.onclick = onclick;
    noti.append(svg, text);
    return noti;
  }

  theme.Notification = new (class {
    noti = null;
    removeTimeoutId = null;
    hideTimeoutId = null;
    transitionDuration = 300;

    show = ({ target, type, message, method = 'after', last = 3000, delay = 0, debug = false, sticky = false }) => {
      this.clearTimeout();
      this.removeNoti();
      setTimeout(() => {
        this.noti = buildNotification({ type, message, onclick: this.handleClick, sticky });
        if (target) target[method](this.noti);
        requestAnimationFrame(() => this.noti.classList.add('show'));
        if (debug) return;
        this.hideTimeoutId = setTimeout(() => {
          this.noti.classList.add('hide');
          this.removeTimeoutId = setTimeout(this.removeNoti, this.transitionDuration * 2);
        }, last);
      }, delay);
    };

    handleClick = () => {
      clearTimeout(this.removeTimeoutId);
      this.noti.classList.add('hide');
      setTimeout(this.removeNoti, this.transitionDuration * 2);
    };

    clearTimeout = () => {
      clearTimeout(this.removeTimeoutId);
      clearTimeout(this.hideTimeoutId);
    };

    removeNoti = () => {
      if (this.noti) this.noti.remove();
    };
  })();
})();
